import { cacheKeys } from "@constants/cache-keys";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { apiAxios } from "@helpers/lib/axios";
import useCacheKey from "@hooks/useCacheKey";
import { API_ENDPOINTS } from "@memorang/constants/urls";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Document, PodcastsResponse } from "../types";

const generateHash = (text: string): number => {
	return text.split("").reduce((acc, char) => {
		return acc + char.charCodeAt(0) * 31;
	}, 0);
};

const getPodcasts = async (
	collectionId: number,
): Promise<PodcastsResponse["documents"]> => {
	const response = await apiAxios.get(API_ENDPOINTS.USER_DOCUMENTS, {
		params: { collectionId },
	});

	return response.data.data.documents ?? [];
};

export const usePodcasts = () => {
	const { currentExamContext } = useExamContext();
	const collectionId = currentExamContext.documentsCollectionId;

	const { baseCacheKey } = useCacheKey();
	const queryEnabled = !!collectionId && !!baseCacheKey;
	const { data: documents, isLoading: isQueryLoading } = useQuery({
		queryKey: [baseCacheKey, cacheKeys.podcasts],
		queryFn: () => (collectionId ? getPodcasts(collectionId) : []),
		enabled: queryEnabled,
	});
	const isLoading = !queryEnabled || isQueryLoading;

	const originalPodcasts = useMemo(() => {
		return documents?.filter((document) => document.type === "PODCAST");
	}, [documents]);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const podcastsByType = useMemo(() => {
		const grouped = new Map<string, Document[]>();

		for (const podcast of originalPodcasts ?? []) {
			const topics = podcast.tags.map((tag) => tag.label || tag.title || "");
			if (Array.isArray(topics) && topics.length > 0) {
				const podcastIdSet = new Set<number>();
				for (const topic of topics) {
					let podcastId = podcast.id;
					const isDuplicate = podcastIdSet.has(podcast.id);
					if (isDuplicate) {
						podcastId = podcast.id + generateHash(topic);
					}
					podcastIdSet.add(podcastId);
					if (!grouped.has(topic)) {
						grouped.set(topic, []);
					}
					grouped.get(topic)!.push({
						...podcast,
						id: podcastId,
						originalId: isDuplicate ? podcast.id : undefined,
					});
				}
			} else {
				if (!grouped.has("Uncategorized")) {
					grouped.set("Uncategorized", []);
				}
				grouped.get("Uncategorized")!.push({ ...podcast, id: podcast.id });
			}
		}

		return Array.from(grouped).map(([type, podcasts]) => {
			return {
				type,
				podcasts,
			};
		});
	}, [originalPodcasts]);

	const podcasts = useMemo(() => {
		const allPodcasts = podcastsByType.flatMap((category) => category.podcasts);
		const uniquePodcasts = new Map<number, Document>();
		for (const podcast of allPodcasts) {
			const uniqueId = podcast.originalId ?? podcast.id;
			if (!uniquePodcasts.has(uniqueId)) {
				uniquePodcasts.set(uniqueId, podcast);
			}
		}
		return Array.from(uniquePodcasts.values());
	}, [podcastsByType]);

	const deduplicatedPodcastsByType = useMemo(() => {
		const seenPodcasts = new Set<number>();
		return podcastsByType
			.map(({ type, podcasts }) => ({
				type,
				podcasts: podcasts.filter((podcast) => {
					const uniqueId = podcast.originalId ?? podcast.id;
					if (seenPodcasts.has(uniqueId)) {
						return false;
					}
					seenPodcasts.add(uniqueId);
					return true;
				}),
			}))
			.filter(({ podcasts }) => podcasts.length > 0);
	}, [podcastsByType]);

	return {
		originalPodcasts,
		podcastsByType: deduplicatedPodcastsByType,
		isLoading,
		podcasts,
	};
};
